import { useCallback, useRef } from 'react';
import { findIndex, uniq } from 'lodash-es';

import { VehicleModel } from 'client/data/models/vehicle-v2';
import { PartnerOfferModel, PARTNER_OFFER_PARAMETERS_PATH } from 'client/data/models/partner-offer-model';
import { delayHeavyTask } from 'client/site-modules/shared/utils/inp-utils';
import {
  validateVin,
  EPO_VIN_INPUT_CONTAINER,
} from 'client/site-modules/shared/utils/appraiser/appraiser-validation-helper';
import { LICENSE_PLATE_AND_VIN, PARTNER_OFFER_CONDITIONS } from 'site-modules/shared/constants/appraisal/appraisal';
import { isLicensePlateAndVinStepValid } from 'site-modules/shared/components/appraisal/trade-in-appraiser/trade-in-appraiser-validators';

const IS_ESCAPE_HATCH = true;

/**
 * Encapsulates all VIN-related state: vin, isVinValid, hasVinMismatchError, isEscapeHatch.
 *
 * @param {Object} params
 * @param {Object} params.state - current appraiser state
 * @param {Function} params.setState - state setter
 * @param {Function} params.setModelValue - luckdragon model setter
 * @param {Object} params.vinStyles - VIN decoded styles from the model
 * @param {Object} params.estimatedVehicle - estimated vehicle from props
 * @param {Function} params.scrollToNextStep - scroll to next step callback
 * @param {Object} params.pendingCallbacksRef - ref queue for post-commit callbacks
 * @param {Object} params.changeStepRef - ref to changeStep (avoids circular deps)
 */
export function useVinManagement({
  state,
  setState,
  setModelValue,
  vinStyles,
  estimatedVehicle,
  scrollToNextStep,
  pendingCallbacksRef,
  changeStepRef,
}) {
  const { vin: stateVin } = state;

  const updateOfferParametersVin = useCallback(
    overrideVin => {
      return setModelValue(PARTNER_OFFER_PARAMETERS_PATH.VIN, PartnerOfferModel, overrideVin || stateVin);
    },
    [stateVin, setModelValue]
  );

  // Ref so changeVin can always call the latest updateOfferParametersVin without it being a dep
  const updateOfferParametersVinRef = useRef(null);
  // eslint-disable-next-line react-hooks/refs -- stable VIN handlers require the latest offer updater.
  updateOfferParametersVinRef.current = updateOfferParametersVin;

  const changeVin = useCallback(
    (vin, isEscapeHatchParam) =>
      new Promise(resolve =>
        delayHeavyTask(() => {
          setState(prev => {
            const errorMap = new Map(prev.errorMap);
            if (errorMap.has(EPO_VIN_INPUT_CONTAINER)) errorMap.delete(EPO_VIN_INPUT_CONTAINER);
            const isVinValid = validateVin(vin);
            if (isVinValid) {
              updateOfferParametersVinRef.current(vin);
            }

            let newState = {
              ...prev,
              vin,
              isVinValid,
              errorMap,
              isEscapeHatch: !!isEscapeHatchParam,
            };

            if (!isEscapeHatchParam) {
              const activeStep = newState.steps.find(step => step.isActive);
              if ([PARTNER_OFFER_CONDITIONS, LICENSE_PLATE_AND_VIN].includes(activeStep?.id)) {
                const isValid = isLicensePlateAndVinStepValid(newState);
                const steps = [...newState.steps];
                const index = findIndex(steps, { id: LICENSE_PLATE_AND_VIN });
                if (index >= 0) {
                  steps[index] = { ...steps[index], isDone: isValid };
                }
                newState = { ...newState, steps };
              }
            }

            return newState;
          });

          setModelValue('estimatedAppraisalVin', VehicleModel, vin);
          resolve();
        })
      ),
    [setModelValue, setState]
  );

  const clearVin = useCallback(() => changeVin(''), [changeVin]);

  const clearVinEscapeHatch = useCallback(() => changeVin('', IS_ESCAPE_HATCH), [changeVin]);

  const updateVinAndContinueEscapeHatch = useCallback(
    vinValue => {
      changeVin(vinValue, IS_ESCAPE_HATCH);
      scrollToNextStep();
    },
    [changeVin, scrollToNextStep]
  );

  const checkVinMismatchError = useCallback(
    vinStylesParam => {
      setState(prev => {
        const { vehicle } = prev;
        const make = vehicle?.make?.slug ?? '';
        const model = vehicle?.model?.slug ?? '';
        const year = vehicle?.year?.year ?? '';
        const { makeNiceId, modelNiceId, year: stylesYear } = vinStylesParam?.['0'] ?? {};
        return {
          ...prev,
          hasVinMismatchError:
            vinStylesParam && !!vinStylesParam.length
              ? uniq([make, makeNiceId, model, modelNiceId, `${year}`, `${stylesYear}`]).length !== 3
              : false,
        };
      });
    },
    [setState]
  );

  const checkVinStylesMismatchError = useCallback(() => {
    const results = vinStyles?.results ?? [];
    const { makeNiceId, modelNiceId, year } = results[0] ?? {};
    setState(prev => ({
      ...prev,
      hasVinMismatchError: results.length
        ? uniq([
            makeNiceId,
            estimatedVehicle?.make?.slug,
            modelNiceId,
            estimatedVehicle?.model?.slug,
            year,
            estimatedVehicle?.year,
          ]).length !== 3
        : false,
    }));
  }, [
    vinStyles?.results,
    setState,
    estimatedVehicle?.make?.slug,
    estimatedVehicle?.model?.slug,
    estimatedVehicle?.year,
  ]);

  const toggleLicensePlateAndVinStepDone = useCallback(() => {
    setState(prev => {
      const activeStep = prev.steps.find(step => step.isActive);

      if (![PARTNER_OFFER_CONDITIONS, LICENSE_PLATE_AND_VIN].includes(activeStep?.id)) {
        return prev;
      }

      const isValid = isLicensePlateAndVinStepValid(prev);
      const steps = [...prev.steps];
      const index = findIndex(steps, { id: LICENSE_PLATE_AND_VIN });
      if (index >= 0) {
        steps[index] = { ...steps[index], isDone: isValid };
        for (let i = index + 1; i < steps.length; i += 1) {
          steps[i] = { ...steps[i], isDone: false };
        }
      }
      return { ...prev, steps };
    });
  }, [setState]);

  const updateVinAndContinueLpAndVin = useCallback(
    (vin, abortChangeStep = false) => {
      changeVin(vin);

      if (!abortChangeStep) {
        pendingCallbacksRef.current.push(() => {
          setState(prev => {
            const lpStep = prev.steps.find(({ id }) => id === LICENSE_PLATE_AND_VIN);
            changeStepRef.current({ stepId: lpStep?.nextId, creativeId: lpStep?.trackingParentId });
            return prev;
          });
        });
      }
    },
    [changeStepRef, changeVin, pendingCallbacksRef, setState]
  );

  return {
    updateOfferParametersVin,
    changeVin,
    clearVin,
    clearVinEscapeHatch,
    updateVinAndContinueEscapeHatch,
    checkVinMismatchError,
    checkVinStylesMismatchError,
    toggleLicensePlateAndVinStepDone,
    updateVinAndContinueLpAndVin,
  };
}
